import { makeId } from "helper/functions";
import PropTypes from "prop-types";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import ProductCard from "Component/ProductCard/ProductCard.component";
import ScrollSwiper from "Component/ScrollSwiper";
import ScrollSwiperSlide from "Component/ScrollSwiperSlide";
import SkeletonComponent from "Component/Skeleton/Skeleton.component";
import ProductListQuery from "Query/ProductList.query";
import { updateNoMatch } from "Store/NoMatch/NoMatch.action";
import { showNotification } from "Store/Notification/Notification.action";
import { noopFn } from "Util/Common";
import { getIndexedProducts } from "Util/Product";
import PRODUCT_TYPE from "Util/Product/Product.config";
import { makeCancelable } from "Util/Promise";
import { prepareQuery } from "Util/Query";
import { executeGet } from "Util/Request";
import { hash } from "Util/Request/Hash";
import { ONE_MONTH_IN_SECONDS } from "Util/Request/QueryDispatcher";
import { mergeProducts } from "Util/Variants";

import {
    SKELETON_SLIDER,
    SKELETON_SLIDER_MOBILE
} from "./ProductListWidget.config";

const PRODUCT_COUNT = 10;
const PRODUCTS_PER_PAGE = 100;
const ID_LENGTH = 10;

function ProductCardSlide({
    product,
    index,
    cardState,
    updateCardState,
    addToCartButton,
    defaultPlacement
}) {
    const slideKey = `${product.id}-slide-${index}`;
    const updateState = useCallback(
        (newPartialState) => updateCardState(slideKey, newPartialState),
        [updateCardState, slideKey]
    );

    return (
        <ScrollSwiperSlide
            swiperClass=""
            buttoHidePrevClass="pr-6!"
            widthClass="max-w-[160px] lg:max-w-[240px]"
        >
            <ProductCard
                product={ product }
                isSwiper
                state={ cardState || {} }
                defaultPlacement={ defaultPlacement }
                addToCartButton={ addToCartButton }
                updateState={ updateState }
                imageClass="aspect-4/5"
            />
        </ScrollSwiperSlide>
    );
}

/** @namespace Bodypwa/Component/ProductListWidget/Component/ProductListWidget */
export function ProductListWidget({
    productsCount = PRODUCT_COUNT,
    productsPerPage = PRODUCTS_PER_PAGE,
    conditionsEncoded = null,
    placement = "",
    addToCartButton = 0
}) {
    const device = useSelector((state) => state.ConfigReducer.device);
    const dispatch = useDispatch();

    const [pages, setPages] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const sliderId = useMemo(() => makeId(ID_LENGTH), []);
    const promiseRef = useRef(null);
    const customerGroupId = useSelector(
        (state) => state.MyAccountReducer.customer.group_id
    );

    const [cardStates, setCardStates] = useState({});
    const updateCardState = useCallback((index, newState) => {
        setCardStates((prev) => ({
            ...prev,
            [index]: { ...(prev[index] || {}), ...newState }
        }));
    }, []);

    const onError = useCallback(
        (error) => {
            dispatch(
                showNotification(
                    "error",
                    __("Error fetching Product List!"),
                    error
                )
            );
            dispatch(updateNoMatch(true));
        },
        [dispatch]
    );

    const swiperItems = useMemo(() => {
        const indexed = pages[sliderId];
        if (!indexed) {
            return [];
        }

        return Object.values(indexed);
    }, [pages, sliderId]);

    const fetchData = useCallback(
        (
            rawQueries,
            onSuccess = noopFn,
            onErr = noopFn,
            broadcastName = "ProductListQuery",
            additionalProducts
        ) => {
            const preparedQuery = prepareQuery(rawQueries);
            const { query, variables } = preparedQuery;
            const queryHash = hash(query + JSON.stringify(variables));

            if (!window.dataCache) {
                window.dataCache = {};
            }

            if (window.dataCache[queryHash]) {
                onSuccess(window.dataCache[queryHash], additionalProducts);
                return;
            }

            const cancelable = makeCancelable(
                executeGet(preparedQuery, broadcastName, ONE_MONTH_IN_SECONDS)
            );

            promiseRef.current = cancelable;

            cancelable.promise.then(
                /** @namespace Bodypwa/Component/ProductListWidget/Component/ProductListWidget/fetchData/useCallback/then */
                (response) => {
                    window.dataCache[queryHash] = response;
                    onSuccess(response, additionalProducts);
                },
                /** @namespace Bodypwa/Component/ProductListWidget/Component/ProductListWidget/fetchData/useCallback/then/onErr/catch */
                (err) => onErr(err)
            );
        },
        []
    );

    const updateProducts = useCallback(
        (data, additionalProducts) => {
            const { products: { items } = {} } = data || {};

            setPages((prev) => {
                const current = prev[sliderId] || additionalProducts;
                if (current) {
                    const { mergedItems } = mergeProducts(
                        current,
                        getIndexedProducts(items),
                        sliderId
                    );

                    return { ...prev, [sliderId]: mergedItems };
                }

                return prev;
            });
        },
        [sliderId]
    );

    const updateProductListItems = useCallback(
        async (data) => {
            const { products: { items } = {} } = data || {};

            const originalProducts = getIndexedProducts(items);

            setIsLoading(false);
            setPages({ [sliderId]: originalProducts });
            const itemsWithVariants = [];
            const itemsWithPrice = [];
            items.forEach((item) => {
                const { attributes, type_id } = item;
                const filtered = attributes?.filter(
                    (attr) =>
                        type_id === PRODUCT_TYPE.configurable &&
                        attr.attribute_options?.length > 1
                );

                if (filtered?.length) {
                    itemsWithVariants.push(item.sku);
                }
                itemsWithPrice.push(item.sku);
            });

            // fetch variants
            const { default: ProductListVariantsQuery } = await import(
                /* webpackChunkName: "widget-product-list-queries" */
                "Query/ProductListVariants.query"
            );

            fetchData(
                [
                    ProductListVariantsQuery.getQuery({
                        args: {
                            filter: { productsSkuArray: itemsWithVariants }
                        },
                        isPdp: false
                    })
                ],
                updateProducts,
                onError,
                `ProductListVariantsQuery${sliderId}`,
                originalProducts
            );

            // fetch prices
            const { default: ProductListPriceQuery } = await import(
                /* webpackChunkName: "widget-product-list-queries" */
                "Query/ProductListPrice.query"
            );

            fetchData(
                [
                    ProductListPriceQuery.getQuery({
                        args: { filter: { productsSkuArray: itemsWithPrice } },
                        isPdp: false
                    })
                ],
                updateProducts,
                onError,
                `ProductListPriceQuery${sliderId}`,
                originalProducts
            );
        },
        [
            fetchData,
            onError,
            productsCount,
            productsPerPage,
            sliderId,
            updateProducts,
            customerGroupId
        ]
    );

    const requestProductList = useCallback(
        (options) => {
            fetchData(
                [ProductListQuery.getQuery({ isWidget: true, ...options })],
                (res) => {
                    updateProductListItems(res);
                },
                onError,
                `ProductListQuery${sliderId}`
            );
        },
        [fetchData, onError, sliderId, updateProductListItems, customerGroupId]
    );

    useEffect(
        () => () => {
            try {
                promiseRef.current?.cancel?.();
            } catch {
                /* ignore */
            }
        },
        []
    );

    useEffect(() => {
        const args = {
            pageSize: productsCount,
            currentPage: 1,
            ...(conditionsEncoded
                ? { filter: { conditions: conditionsEncoded } }
                : {})
        };

        requestProductList({ args });
    }, [productsCount, conditionsEncoded, requestProductList]);

    if (isLoading || swiperItems.length === 0) {
        return (
            <SkeletonComponent
                items={ device.isMobile ? SKELETON_SLIDER_MOBILE : SKELETON_SLIDER }
            />
        );
    }

    return (
        <ScrollSwiper
            className="pb-6 no-scrollbar"
            swiperWrapperClass="px-4 lg:px-0 gap-2 lg:gap-4"
        >
            { swiperItems.map((product, i) => (
                <ProductCardSlide
                    key={ product.id || `slide-${i}` }
                    product={ product }
                    index={ i }
                    addToCartButton={ addToCartButton }
                    defaultPlacement={ placement }
                    cardState={ cardStates[`${product.id}-slide-${i}`] }
                    updateCardState={ updateCardState }
                />
            )) }
        </ScrollSwiper>
    );
}

ProductListWidget.propTypes = {
    productsCount: PropTypes.number,
    productsPerPage: PropTypes.number,
    conditionsEncoded: PropTypes.string
};

export default ProductListWidget;
